/* --external-- */
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

/* --internal-- */
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tag,
  TextInput,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  AddOutlined,
  DeleteOutlined,
  ImageSquareOutlined,
  ReloadArrowOutlined,
  RootCrossOutlined,
  VideoOutlined,
  VolumeHighOutlined,
} from "@toddle-edu/ds-icons";
import { colors, fontStyles } from "@toddle-edu/ds-theme";
import { generateRandomIdV2 } from "Utils";
import {
  MODE_TEACHER_CREATE,
  DOC_EDITOR_STYLES,
  ALLOWED_AUDIO_FILE_TYPES,
  ALLOWED_VIDEO_FILE_TYPES,
} from "Worksheet/modules/WorksheetConstants";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import MiniDocEditor from "UIComponents/DocEditor/MiniDocEditorWrapper";

/* --relative-- */
import classes from "./MatchingField.scss";
import MatchingView from "./MatchingView";
import {
  getAnswers,
  getFilteredOptions,
  getReusedIndex,
  handleMediaUpload,
  updateReusedAnswers,
  updateReusedFrom,
} from "./MatchingFieldUtils";

/* --styles-- */
const styles = {
  addPadding: {
    padding: "8px",
  },
  addPaddingLeft: {
    paddingLeft: "8px",
  },
  addPaddingRightLeft: {
    padding: "0 8px",
  },
  dropdownStyles: {
    padding: "8px",
    gap: "8px",
    display: "flex",
  },
  fixedWidth: {
    width: "250px",
  },
  reuseIconStyles: {
    padding: "4px 8px 0",
  },
  rowIndex: {
    width: "36px",
    textAlign: "center",
    color: colors.textSubtle,
    ...fontStyles.medium,
    flexShrink: "0",
  },
};

/*
  @params { reuseOptions, onReuse, currentAnswerId, matchingPairs }
  @return Dropdown to reuse answers from other prompts
*/
const ReuseOptionsDropdown = ({
  reuseOptions,
  onReuse,
  currentAnswerId,
  matchingPairs,
}) => {
  //  Filter the reuse options
  const filteredReuseOptions = getFilteredOptions(
    reuseOptions,
    currentAnswerId,
    matchingPairs
  );

  const disableDropdown = _.isEmpty(
    _.get(filteredReuseOptions, [0, "options"], [])
  );

  return (
    <Dropdown
      disabled={disableDropdown}
      overlay={
        <DropdownMenu
          options={filteredReuseOptions}
          onClick={({ key }) => onReuse(key)}
          testId="MatchingField-addReusedId"
        />
      }
    >
      <IconButton
        dsVersion="2.0"
        type="plain"
        variant="neutral"
        size="medium"
        _legacy_variant="plain"
        _legacy_size="medium"
        icon={<ReloadArrowOutlined variant="subtle" />}
        testId="MatchingField-openDropdownMenu"
      />
    </Dropdown>
  );
};

// * Options for media dropdown
const mediaActions = [
  {
    key: "image",
    icon: <ImageSquareOutlined variant="subtle" />,
    accept: "image/*",
  },
  {
    key: "audio",
    icon: <VolumeHighOutlined variant="subtle" />,
    accept: _.join(ALLOWED_AUDIO_FILE_TYPES, ","),
  },
  {
    key: "video",
    icon: <VideoOutlined variant="subtle" />,
    accept: _.join(ALLOWED_VIDEO_FILE_TYPES, ","),
  },
];

// * Media dropdown component for adding images, audio, video, and math equations
const MediaDropdown = ({
  itemId,
  context, // 'prompt' or 'answer'
  handleMediaClick,
  handleDropdownVisibilityChange,
  getMediaImperativeHandler,
  mediaDropdownVisibility,
  disableDropdown,
}) => {
  return (
    <Dropdown
      disabled={disableDropdown}
      overlay={
        <div className={classes.dropdownMenu}>
          {_.map(mediaActions, action => (
            <IconButton
              key={action.key}
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              size="small"
              icon={action.icon}
              onClick={() => {
                handleMediaClick(action.accept, context, itemId);
                handleDropdownVisibilityChange(itemId, false);
              }}
              onMouseDown={e => e.preventDefault()}
            />
          ))}

          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="plain"
            size="small"
            icon={<RootCrossOutlined variant="subtle" />}
            onClick={() => {
              const handler = _.get(
                getMediaImperativeHandler("image", context, itemId),
                "current"
              );

              if (_.isFunction(handler?.openMathsEqEditor)) {
                handler.openMathsEqEditor();
              }
            }}
            onMouseDown={e => e.preventDefault()}
          />
        </div>
      }
      visible={mediaDropdownVisibility[itemId] && !disableDropdown}
      onVisibleChange={value => handleDropdownVisibilityChange(itemId, value)}
    >
      <IconButton
        dsVersion="2.0"
        variant="neutral"
        type="plain"
        icon={<AddOutlined variant="subtle" />}
      />
    </Dropdown>
  );
};

// * Component for matching field in edit mode
const MatchingDocEditorEdit = props => {
  const {
    getMediaImperativeHandler,
    handleChange,
    index,
    t,
    context,
    itemValue,
    itemId,
  } = props;
  return (
    <MiniDocEditor
      saveDelay={300}
      viewOnly={false}
      isSlashMenuEnabled={true}
      value={itemValue}
      placeholderText={t("common:typeHere")}
      width={250}
      minHeight={38}
      containerStyle={DOC_EDITOR_STYLES.MINI_EDITOR_CONTAINER_STYLES}
      styles={DOC_EDITOR_STYLES.MINI_EDITOR_MATCHING_STYLES}
      updateInputField={(value, isEmpty) => {
        const newValue = isEmpty ? "" : value;
        handleChange(index, newValue);
      }}
      imageInsertionImperativeHandler={getMediaImperativeHandler(
        "image",
        context,
        itemId
      )}
      embedInsertionImperativeHandler={getMediaImperativeHandler(
        "embed",
        context,
        itemId
      )}
      floatingMiniEditorToolbarConfig={{ isEnabled: true }}
      editorClassName={classes.miniEditorContainer}
      isPortalToBody={true}
    />
  );
};

// * Component for matching field in view mode
const MatchingDocEditorView = props => {
  const { itemValue } = props;
  return (
    <MiniDocEditor
      value={itemValue}
      viewOnly={true}
      width={"100%"}
      containerStyle={DOC_EDITOR_STYLES.MINI_EDITOR_CONTAINER_STYLES}
      styles={DOC_EDITOR_STYLES.MINI_EDITOR_MATCHING_STYLES_VIEW}
      editorClassName={classes.miniEditorContainer}
    />
  );
};

/*
  @params { t, matchingPairs, setMatchingPairs }
  @return Matching field for teacher create mode
*/
const Matching = props => {
  const { t, matchingPairsFromField, onChangeMatchingPair } = props;

  const showDocEditor = getIsWorksheetDocEditorEnabled();
  const [mediaDropdownVisibility, setMediaDropdownVisibility] = useState({});
  const [currentUploadContext, setCurrentUploadContext] = useState(null);

  // Add an empty row at creation
  useEffect(() => {
    if (_.isEmpty(matchingPairsFromField)) {
      addNewMatchingPair();
    }
  }, [matchingPairsFromField]);

  //  Add an empty row
  const addNewMatchingPair = () => {
    const newMatchingPair = {
      prompt: { id: generateRandomIdV2(), value: "", reusedFrom: "" },
      answer: { id: generateRandomIdV2(), value: "" },
    };
    onChangeMatchingPair([...matchingPairsFromField, newMatchingPair]);
  };

  //  Delete a row of prompt and answer
  const removeMatchingPair = index => {
    const elementToDelete = matchingPairsFromField[index];
    const promptId = _.get(elementToDelete, "prompt.id");
    const answerId = _.get(elementToDelete, "answer.id");

    // Clean up refs for the deleted element
    if (promptId && mediaImperativeHandler.current) {
      delete mediaImperativeHandler.current[`image-prompt-${promptId}`];
      delete mediaImperativeHandler.current[`embed-prompt-${promptId}`];
    }
    if (answerId && mediaImperativeHandler.current) {
      delete mediaImperativeHandler.current[`image-answer-${answerId}`];
      delete mediaImperativeHandler.current[`embed-answer-${answerId}`];
    }

    // Clean up dropdown visibility state for both prompt and answer
    setMediaDropdownVisibility(prev => {
      const updated = { ...prev };
      if (promptId) {
        delete updated[promptId];
      }
      if (answerId) {
        delete updated[answerId];
      }
      return updated;
    });

    const updatedMatchingPair = updateReusedAnswers(
      matchingPairsFromField,
      elementToDelete
    );
    const updatedMatchingPairList = _.filter(
      updatedMatchingPair,
      (_, i) => i !== index
    );
    onChangeMatchingPair(updatedMatchingPairList);
  };

  //  Handle changes in prompt's value
  const handlePromptChange = (index, value) => {
    const updatedMatchingPairList = _.cloneDeep(matchingPairsFromField);
    if (_.has(updatedMatchingPairList[index], "prompt")) {
      _.set(updatedMatchingPairList, [index, "prompt"], {
        ..._.get(updatedMatchingPairList, [index, "prompt"], {}),
        value,
      });
    }
    onChangeMatchingPair(updatedMatchingPairList);
  };

  //  Handle changes in answer's value
  const handleAnswerChange = (index, value) => {
    const updatedMatchingPairList = _.cloneDeep(matchingPairsFromField);
    if (_.has(updatedMatchingPairList[index], "answer")) {
      _.set(updatedMatchingPairList, [index, "answer"], {
        ..._.get(updatedMatchingPairList, [index, "answer"], {}),
        value,
      });
    }
    onChangeMatchingPair(updatedMatchingPairList);
  };

  //  Reuse an answer from another prompt
  const addReusedAnswer = (key, index) => {
    const updatedMatchingPairList = _.cloneDeep(matchingPairsFromField);
    let promptItem = _.get(updatedMatchingPairList, [key - 1, "prompt"], {});

    // check if the reused prompt itself is being reused
    const reusedFromId = _.get(promptItem, "reusedFrom", null);

    if (reusedFromId) {
      const matchingPairItem = _.find(
        updatedMatchingPairList,
        pair => _.get(pair, "prompt.id", null) === reusedFromId
      );
      promptItem = _.get(matchingPairItem, "prompt", {});
    }

    const newPromptId = _.get(promptItem, "id", null);
    const oldPromptId = _.get(
      updatedMatchingPairList,
      [index, "prompt", "id"],
      null
    );

    _.set(updatedMatchingPairList, [index], {
      prompt: {
        ..._.get(updatedMatchingPairList, [index, "prompt"], {}),
        reusedFrom: _.get(promptItem, "id", ""),
      },
      answer: { ..._.get(updatedMatchingPairList, [key - 1, "answer"], {}) },
    });

    //  Update reused id of prompts which are using the current prompt
    const finalMatchingPairList = updateReusedFrom(
      updatedMatchingPairList,
      oldPromptId,
      newPromptId
    );
    onChangeMatchingPair(finalMatchingPairList);
  };

  //  Remove a reused answer and set an empty answer
  const removeReusedAnswer = index => {
    const updatedMatchingPairList = _.cloneDeep(matchingPairsFromField);

    const currentPair = _.get(updatedMatchingPairList, [index], {});
    const currentPrompt = _.get(currentPair, "prompt", {});

    const updatedPrompt = { ...currentPrompt, reusedFrom: "" };
    const newAnswer = { id: generateRandomIdV2(), value: "" };

    const newPair = {
      ...currentPair,
      prompt: updatedPrompt,
      answer: newAnswer,
    };

    _.set(updatedMatchingPairList, [index], newPair);
    onChangeMatchingPair(updatedMatchingPairList);
    return;
  };

  //  All the options available for prompts to reuse
  const reuseOptions = [
    {
      isItemGroup: true,
      key: "REUSED_FROM",
      label: t("worksheet:reuse_answer_from"),
      options: _.map(matchingPairsFromField, (item, index) => ({
        key: index + 1,
        label: t("worksheet:row_with_count", {
          count: index + 1,
        }),
        promptId: _.get(item, "prompt.id", null),
        answerId: _.get(item, "answer.id", null),
      })),
    },
  ];

  // Refs for image and embed insertion in mini doc editor
  const mediaImperativeHandler = useRef({});
  const sharedMediaRef = useRef();

  // Helper function to get or create refs for each pair using unique IDs
  const getMediaImperativeHandler = (mediaType, context, itemId) => {
    const key = `${mediaType}-${context}-${itemId}`; // e.g., 'image-prompt-abc123', 'embed-answer-def456'
    if (
      !mediaImperativeHandler.current ||
      !mediaImperativeHandler.current[key]
    ) {
      mediaImperativeHandler.current[key] = { current: null };
    }
    return mediaImperativeHandler.current[key];
  };

  // Helper function to handle dropdown visibility for each pair using unique IDs
  const handleDropdownVisibilityChange = (itemId, isVisible) => {
    setMediaDropdownVisibility(prev => ({
      ...prev,
      [itemId]: isVisible,
    }));
  };

  // Helper function to handle media button click and trigger file input
  const handleMediaClick = (accept, context, itemId) => {
    // Store which row, context, and accept type we're uploading for
    setCurrentUploadContext({ accept, context, itemId });

    const input = sharedMediaRef.current;
    if (input) {
      input.accept = accept;
      input.value = "";
      input.click();
    }
  };

  return (
    <>
      {/* Field header */}
      <div>
        <div className={classes.gridContainer}>
          <div>
            <div className="text-label-2">{t("worksheet:prompts")}</div>
          </div>
          <div>
            <div className="text-label-2">{t("worksheet:answers")}</div>
          </div>
        </div>

        <div className={classes.gridContainer}>
          <div className={classes.promptContainer}>
            {t("worksheet:prompt_header")}
          </div>
          <div className={classes.promptContainer}>
            {t("worksheet:answer_header")}
          </div>
        </div>
      </div>

      {/* Field body */}
      <div>
        {_.map(matchingPairsFromField, (item, index) => (
          <div
            key={`${_.get(item, "prompt.id", "prompt")}-${_.get(
              item,
              "answer.id",
              "answer"
            )}`}
            className={classes.rowContainer}
          >
            {/* Prompt */}
            <div className={classes.cellContainer}>
              <div
                style={styles.addPaddingRightLeft}
                className={classes.rowIndex}
              >
                {index + 1}.
              </div>
              {showDocEditor ? (
                <MatchingDocEditorEdit
                  getMediaImperativeHandler={getMediaImperativeHandler}
                  handleChange={handlePromptChange}
                  index={index}
                  t={t}
                  context="prompt"
                  itemValue={_.get(item, "prompt.value", "")}
                  itemId={_.get(item, "prompt.id", null)}
                />
              ) : (
                <TextInput
                  value={_.get(item, "prompt.value", "")}
                  onChange={e => handlePromptChange(index, e.target.value)}
                  size="medium"
                  placeholder={t("common:typeHere")}
                  style={styles.fixedWidth}
                  testId="MatchingField-changePrompt"
                />
              )}

              {/* Prompt media options */}
              {showDocEditor && (
                <div className={classes.iconContainer}>
                  <MediaDropdown
                    itemId={_.get(item, "prompt.id")}
                    context="prompt"
                    handleMediaClick={handleMediaClick}
                    handleDropdownVisibilityChange={
                      handleDropdownVisibilityChange
                    }
                    getMediaImperativeHandler={getMediaImperativeHandler}
                    mediaDropdownVisibility={mediaDropdownVisibility}
                  />
                </div>
              )}
            </div>

            {/* Answer */}
            <div className={classes.cellContainerRight}>
              {_.get(item, "prompt.reusedFrom") ? (
                <Tag
                  _legacy_size="small"
                  dsVersion="2.0"
                  size="medium"
                  onClose={() => removeReusedAnswer(index)}
                >
                  <div className={classes.reusedContainer}>
                    <ReloadArrowOutlined variant="subtle" size={"xxx-small"} />
                    {t("worksheet:reused_from_id", {
                      id: getReusedIndex(matchingPairsFromField, item),
                    })}
                  </div>
                </Tag>
              ) : showDocEditor ? (
                <MatchingDocEditorEdit
                  getMediaImperativeHandler={getMediaImperativeHandler}
                  handleChange={handleAnswerChange}
                  index={index}
                  t={t}
                  context="answer"
                  itemValue={_.get(item, "answer.value", "")}
                  itemId={_.get(item, "answer.id", null)}
                />
              ) : (
                <TextInput
                  value={_.get(item, "answer.value", "")}
                  onChange={e => handleAnswerChange(index, e.target.value)}
                  size="medium"
                  placeholder={t("common:typeHere")}
                  style={styles.fixedWidth}
                  testId="MatchingField-changeAnswer"
                />
              )}

              {/* Answer options */}
              <div className={classes.iconContainer}>
                {showDocEditor && (
                  <MediaDropdown
                    itemId={_.get(item, "answer.id")}
                    context="answer"
                    handleMediaClick={handleMediaClick}
                    handleDropdownVisibilityChange={
                      handleDropdownVisibilityChange
                    }
                    getMediaImperativeHandler={getMediaImperativeHandler}
                    mediaDropdownVisibility={mediaDropdownVisibility}
                    disableDropdown={_.get(item, "prompt.reusedFrom", "")}
                  />
                )}
                <ReuseOptionsDropdown
                  reuseOptions={reuseOptions}
                  onReuse={key => addReusedAnswer(key, index)}
                  currentAnswerId={_.get(item, "answer.id")}
                  matchingPairs={matchingPairsFromField}
                />

                <IconButton
                  dsVersion="2.0"
                  type="plain"
                  variant="neutral"
                  _legacy_variant="plain"
                  style={styles.addPadding}
                  onClick={() => removeMatchingPair(index)}
                  icon={<DeleteOutlined size={"xx-small"} />}
                  testId="MatchingField-deleteRow"
                  disabled={_.size(matchingPairsFromField) === 1}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add row button */}
        <div className={classes.addRowContainer}>
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="inline"
            size="medium"
            _legacy_variant="inline-progressive"
            _legacy_size="small"
            onClick={addNewMatchingPair}
            icon={<AddCircleOutlined />}
            testId="MatchingField-addRow"
          >
            {t("worksheet:add_row")}
          </Button>
        </div>

        {/* Single shared file input for all media uploads */}
        {showDocEditor && (
          <input
            type="file"
            style={{ display: "none" }}
            ref={sharedMediaRef}
            onChange={e => {
              handleMediaUpload({
                file: e.target.files[0],
                getMediaImperativeHandler,
                currentUploadContext,
                setCurrentUploadContext,
              });
            }}
          />
        )}
      </div>
    </>
  );
};

const MatchingInactive = props => {
  const { t, matchingPairsFromField } = props;

  const showDocEditor = getIsWorksheetDocEditorEnabled();

  const labelClasses = classNames("text-label-l", classes.textDefault);

  const matchingFieldClasses = classNames(
    "text-body-l",
    classes.textDefault,
    "w-full",
    "min-w-0"
  );

  return (
    <>
      {/* Field header */}
      <div className={classes.gridContainer}>
        <div className={labelClasses}>{t("worksheet:prompts")}</div>
        <div className={labelClasses}>{t("worksheet:answers")}</div>
      </div>

      {/* Field body */}
      <div>
        {_.map(matchingPairsFromField, (item, index) => {
          const cellContainerClasses = classNames(
            classes.cellContainer,
            classes.cellContainerV2
          );

          const cellContainerRightClasses = classNames(
            classes.cellContainerRight,
            classes.cellContainerRightV2,
            {
              [classes.reuseCellContainerRevamp]: _.get(
                item,
                "prompt.reusedFrom"
              ),
            }
          );

          const rowContainerClasses = classNames(
            classes.rowContainer,
            classes.rowContainerV2
          );

          return (
            <div
              key={`${_.get(item, "prompt.id", "prompt")}-${_.get(
                item,
                "answer.id",
                "answer"
              )}`}
              className={rowContainerClasses}
            >
              {/* Prompt */}
              <div className={cellContainerClasses}>
                <div style={styles.rowIndex}>{index + 1}.</div>
                <div className={matchingFieldClasses}>
                  {showDocEditor ? (
                    <MatchingDocEditorView
                      itemValue={_.get(item, "prompt.value", "")}
                    />
                  ) : (
                    _.get(item, "prompt.value", "")
                  )}
                </div>
              </div>

              {/* Answer */}
              <div className={cellContainerRightClasses}>
                {_.get(item, "prompt.reusedFrom") ? (
                  <Tag _legacy_size="small" dsVersion="2.0" size="medium">
                    <div className={classes.reusedContainer}>
                      <ReloadArrowOutlined
                        variant="subtle"
                        size={"xxx-small"}
                      />
                      {t("worksheet:reused_from_id", {
                        id: getReusedIndex(matchingPairsFromField, item),
                      })}
                    </div>
                  </Tag>
                ) : (
                  <div className={matchingFieldClasses}>
                    {showDocEditor ? (
                      <MatchingDocEditorView
                        itemValue={_.get(item, "answer.value", "")}
                      />
                    ) : (
                      _.get(item, "answer.value", "")
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

/*
  @params { t, mode, updateInputField, matchingPairsFromField, matchingResponseFromField }
  @return Matching Field component
*/
const MatchingField = props => {
  const {
    t,
    mode,
    fieldMode,
    updateInputField,
    showViewSolutionButton,
    matchingPairsFromField,
    matchingResponseFromField,
    isSubmitted,
  } = props;

  const onChangeMatchingPair = updateMatchingPair => {
    const matchingPairsAnswers = getAnswers(updateMatchingPair);
    updateInputField({
      params: {
        value: { matchingPairs: updateMatchingPair },
        matchingPairsAnswers,
      },
      isDebounce: true,
      isMutation: true,
    });
  };

  const isInactiveQuestionRevamp =
    mode === MODE_TEACHER_CREATE && _.isEqual(fieldMode, "preview");

  const containerClasses = classNames({
    [classes.container]: !isInactiveQuestionRevamp,
    [classes.containerV2]: isInactiveQuestionRevamp,
  });

  return (
    <div className={containerClasses}>
      {isInactiveQuestionRevamp ? (
        <MatchingInactive
          t={t}
          matchingPairsFromField={matchingPairsFromField}
        />
      ) : _.includes(["view", "edit"], fieldMode) ? (
        <MatchingView
          t={t}
          mode={mode}
          matchingPairs={matchingPairsFromField}
          matchingResponseFromField={matchingResponseFromField}
          updateInputField={updateInputField}
          showViewSolutionButton={showViewSolutionButton}
          isSubmitted={isSubmitted}
        />
      ) : _.includes(["create"], fieldMode) ? (
        <Matching
          t={t}
          onChangeMatchingPair={onChangeMatchingPair}
          matchingPairsFromField={matchingPairsFromField}
        />
      ) : null}
    </div>
  );
};

MatchingField.propTypes = {
  t: PropTypes.func.isRequired,
  mode: PropTypes.string,
  fieldMode: PropTypes.string,
  updateInputField: PropTypes.func,
  matchingPairsFromField: PropTypes.array,
  matchingResponseFromField: PropTypes.object,
  showViewSolutionButton: PropTypes.bool,
  isSubmitted: PropTypes.bool,
};

MatchingField.defaultProps = {
  mode: "view",
  fieldMode: "view",
  matchingPairsFromField: [],
  matchingResponseFromField: {},
  showViewSolutionButton: false,
  isSubmitted: false,
};

export default MatchingField;
